<script lang="ts">
  import { Format } from '../lib/format';
  import type { CostBar } from '../lib/cost-bar';

  interface Props {
    title: string;
    bars: CostBar[];
    empty: string;
    onOpen?: (key: string) => void;
  }

  let { title, bars, empty, onOpen }: Props = $props();

  // A barra é proporcional à MAIOR da lista, não ao total: com uma linha de 90%
  // as outras viravam risquinhos indistinguíveis.
  const peak = $derived(Math.max(...bars.map((bar) => bar.weight), 0));
  const total = $derived(bars.reduce((soma, bar) => soma + bar.weight, 0));

  function width(weight: number): number {
    if (peak <= 0) return 0;
    return Math.max(2, (weight / peak) * 100);
  }
</script>

<section class="panel">
  <h3 class="panel-title">{title}</h3>

  {#if bars.length === 0}
    <p class="panel-empty">{empty}</p>
  {:else}
    <ul class="bars">
      {#each bars as bar (bar.key)}
        <li class="bar">
          <div class="bar-head">
            {#if onOpen}
              <button class="bar-label link" onclick={() => onOpen(bar.key)}>{bar.label}</button>
            {:else}
              <span class="bar-label">{bar.label}</span>
            {/if}
            <span class="bar-value mono">
              {bar.value}
              {#if bar.share}
                <span class="bar-share">{Format.percent(bar.weight, total)}</span>
              {/if}
            </span>
          </div>
          <div class="bar-track">
            <span class="bar-fill" style="width: {width(bar.weight)}%"></span>
          </div>
          {#if bar.sub}<span class="bar-sub mono">{bar.sub}</span>{/if}
          {#if bar.metrics.length > 0}
            <dl class="metrics">
              {#each bar.metrics as metric (metric.label)}
                <div class="metric">
                  <dt>{metric.label}</dt>
                  <dd>{metric.value}<span class="metric-sub mono">{metric.sub}</span></dd>
                </div>
              {/each}
            </dl>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
  }
  .panel-title {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
  }
  .panel-empty {
    margin: 0;
    font-size: 12px;
    color: var(--text-tertiary);
  }
  .bars {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }
  .bar-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .bar-label {
    font-size: 15px;
    font-weight: 500;
    color: var(--text-primary);
    overflow-wrap: anywhere;
  }
  .bar-label.link {
    padding: 0;
    font: inherit;
    background: none;
    border: none;
    border-bottom: 1px solid transparent;
    color: var(--text-primary);
    cursor: pointer;
    text-align: left;
  }
  .bar-label.link:hover {
    color: var(--color-success);
    border-bottom-color: var(--color-success);
  }
  .bar-label.link:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }
  .bar-value {
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--text-primary);
    white-space: nowrap;
  }
  .bar-share {
    color: var(--text-tertiary);
    font-size: 11px;
    margin-left: var(--space-1);
  }
  /* As quatro medidas ficam numa grade fixa: alinhadas de uma barra para a
     outra, o olho compara a coluna inteira em vez de ler frase por frase. */
  .metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-2);
    margin: var(--space-1) 0 0;
    padding: var(--space-2) 0 0;
    border-top: 1px solid var(--border-subtle);
  }
  .metric {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .metric dt {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
  }
  .metric dd {
    display: flex;
    flex-direction: column;
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }
  .metric-sub {
    font-size: 11px;
    font-weight: 400;
    color: var(--text-tertiary);
  }
  .bar-track {
    height: 6px;
    background: var(--bg-base, rgba(255, 255, 255, 0.04));
    border-radius: 999px;
    overflow: hidden;
  }
  .bar-fill {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, #2f9e63 0%, var(--color-success) 100%);
    border-radius: 999px;
  }
  .bar-sub {
    font-size: 11px;
    color: var(--text-tertiary);
  }
</style>
